import { Injectable } from '@angular/core';
import { CoreProvider } from '../core.provider';
import { Team } from './teams.service';
import { CustomEliteConfigurationButtonModelDto, TeamModelDto } from '../api/models';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { Mutex } from '../auth/mutex';
import { mapCloudGameToLocalGame, mapLocalGameToCloudGame } from './games.service.helper';

export const PRESIGNED_CLOUD_VIDEO_EXPIRATION_TIME_MS = 1000 * 60 * 60 * 3; // 3 hours

export type EventType = 'ATTACK_FAULT_COMMIT'
    | 'TECHNICAL_MISTAKE_COMMIT'
    | 'PASSIVE'
    | '1-1_WON'
    | '2_MIN'

    | 'GOAL'
    | 'LOST_BALL'
    | 'POST_OUT'
    | 'SAVE'

    | 'FOUL_RECEIVE'
    | '7M_PROVOKE'

    | 'YELLOW_CARD'
    | '2MIN_PROVOKE'
    | '7M_PROVOKE_SUSPENSION'
    | 'RED_CARD'

    | 'SHOT_BLOCKED';

export type PhaseType = 'FAST_BREAK' | 'OFFENSE_POSITIONAL' | 'COUNTER_GOAL';
export type OffenseSystemType = 'EQUALITY' | 'EQUALITY-5+1vs6' | 'EQUALITY-6vs5+1' | 'INFERIORITY-5vs6' | 'INFERIORITY-6vs7' | 'INFERIORITY-OTHER' | 'SUPERIORITY-6vs5' | 'SUPERIORITY-7vs6' | 'SUPERIORITY-OTHER';
export type DefenseSystemType = '6:0' | '5:1' | 'OTHER' | '3:2:1' | '4:2';

export type GameSystemItem = {
  actionDisplayName: string;
  colorCode: string;
  actionName: string;
  /**
   * Free text typed while tagging, only ever set by a `text` type custom elite button. It rides
   * along inside the event's `gameSystem` payload — which is persisted as a JSON string on both
   * sides — so it reaches the cloud and the paired device without any extra plumbing.
   */
  text?: string;
};

export interface EventDto {
  uuid?: string;
  localId: string;
  orderId: number;
  gameLocalId: string;
  teamMarker: 'home' | 'visitor';
  eventDate: Date;
  important: boolean;
  videoUri: string;
  lastPresignedCloudVideoTimestamp?: number;
  lastPresignedCloudVideoUrl?: string;
  failedVideoUploaded?: number;
  cloudVideoUploaded: boolean;
  pendingUpdate?: boolean;
  videoBlob?: Blob;

  // Elite
  phase?: PhaseType;
  eventType?: EventType;
  offenseSystem?: OffenseSystemType;
  defenseSystem?: DefenseSystemType;
  gameSecondsSinceStart?: number;
  halftime?: string;
  minutesSinceHalftime?: number;
  secondsSinceHalftime?: number;
  gameSystem?: GameSystemItem[]
}

export type GameModelSnapshotDto = {
  customEliteButtons?: CustomEliteConfigurationButtonModelDto[];
}
export type GameModeType = 'rookie' | 'elite';
export interface GameDto {
  uuid?: string;
  gameMode: GameModeType;
  gameModelSnapshot: GameModelSnapshotDto;
  trackerMode: 'camera' | 'bench';
  gameStatus: 'ended' | 'live';
  accessHash: string;
  localId: string;
  localConnectionInfo?: string;
  /**
   * HLS playlist of an external camera to record from, instead of this device's own camera.
   *
   * Local-only on purpose: it never reaches the backend and the cloud mappers drop it. The bench does
   * not need it — it keeps consuming clips from `/static/` and S3 exactly as before — while the camera
   * device does need it to survive an app restart mid-match, which storing it on the game achieves.
   */
  externalCameraUrl?: string;
  gameDate: string;
  homeTeam: Team;
  visitorTeam: Team;
  createdAt: string;
  localLastUpdate: number | null;
  pendingDelete: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class GamesService {
  private readonly GAMES_STORAGE_KEY = 'games';
  private readonly EVENTS_STORAGE_KEY_PREFIX = 'events_';
  private core?: CoreProvider;

  private gamesSubject = new BehaviorSubject<GameDto[]>([]);
  public get games$(): Observable<GameDto[]> {
    return this.gamesSubject.asObservable();
  }
  public get games(): GameDto[] {
    return this.gamesSubject.value;
  }

  private postLiveUploadManagerSubject = new BehaviorSubject<{ game: GameDto, events: EventDto[], status: 'pending' | 'uploading' | 'done', progress: number }[]>([]);
  public get postLiveUploadManager$(): Observable<{ game: GameDto, events: EventDto[], status: 'pending' | 'uploading' | 'done', progress: number }[]> {
    return this.postLiveUploadManagerSubject.asObservable();
  }

  constructor() { }

  async initCore(core: CoreProvider) {
    this.core = core;
    this.getGames();
  }

  async getGames(): Promise<GameDto[]> {
    const gamesJson = await this.core!.storage.getItem(this.GAMES_STORAGE_KEY);
    const games = gamesJson ? (JSON.parse(gamesJson) as GameDto[]).sort((a, b) => new Date(b.gameDate).getTime() - new Date(a.gameDate).getTime()) : [];
    this.gamesSubject.next(games);
    return games;
  }

  async addGame(game: Omit<GameDto, 'createdAt'>): Promise<GameDto[]> {
    const games = (await this.getGames()).filter(g => g.localId !== game.localId);
    games.push({ ...game, createdAt: new Date().toISOString() });
    await this.updateGames(games);
    return games;
  }

  async updateGame(game: GameDto): Promise<GameDto[]> {
    const games = await this.getGames();
    const gameIndex = games.findIndex(g => g.localId === game.localId);
    if (gameIndex === -1) throw new Error('Game not found');
    games[gameIndex] = game;
    await this.updateGames(games);
    return games;
  }

  private async updateGames(games: GameDto[], preventCloudUpdate: boolean = false): Promise<void> {
    games = games.sort((a, b) => new Date(b.gameDate).getTime() - new Date(a.gameDate).getTime());
    await this.core!.storage.setItem(this.GAMES_STORAGE_KEY, JSON.stringify(games));
    this.gamesSubject.next(games);
    if (!preventCloudUpdate) {
      try {
        await this.syncToCloud();
      } catch (error) {
        console.error('Error syncing games to cloud', error);
      }
    }
  }

  async deleteGame(gameId: string): Promise<void> {
    let games = await this.getGames();
    const gameToDelete = games.find(game => game.localId === gameId);
    if (gameToDelete?.uuid) {
      gameToDelete.pendingDelete = true;
    } else {
      games = games.filter(game => game.localId !== gameId);
    }
    await this.updateGames(games);
  }

  async addGameEvent(event: EventDto): Promise<void> {
    const games = await this.getGames();
    const game = games.find(game => game.localId === event.gameLocalId);
    if (!game) throw new Error('Game not found');

    const gameEventsKey = `${this.EVENTS_STORAGE_KEY_PREFIX}${event.gameLocalId}`;
    const gameEvents = await this.core!.storage.getItem(gameEventsKey);
    const events = gameEvents ? JSON.parse(gameEvents) : [];
    events.push(event);
    await this.core!.storage.setItem(gameEventsKey, JSON.stringify(events));
  }

  async getGameEvents(gameLocalId: string): Promise<EventDto[]> {
    const gameEventsKey = `${this.EVENTS_STORAGE_KEY_PREFIX}${gameLocalId}`;
    const gameEvents = await this.core!.storage.getItem(gameEventsKey);
    return gameEvents ? JSON.parse(gameEvents) : [];
  }

  async deleteGameEvent(gameLocalId: string, eventLocalId: string): Promise<EventDto[]> {
    const actualEvents = await this.getGameEvents(gameLocalId);
    const filteredEvents = actualEvents.filter(event => event.localId !== eventLocalId);
    const gameEventsKey = `${this.EVENTS_STORAGE_KEY_PREFIX}${gameLocalId}`;
    await this.core!.storage.setItem(gameEventsKey, JSON.stringify(filteredEvents));
    return filteredEvents;
  }

  async updateGameEvent(event: EventDto): Promise<EventDto[]> {
    const actualEvents = await this.getGameEvents(event.gameLocalId);
    const filteredEvents = actualEvents.map(e => e.localId === event.localId ? event : e);
    const gameEventsKey = `${this.EVENTS_STORAGE_KEY_PREFIX}${event.gameLocalId}`;
    await this.core!.storage.setItem(gameEventsKey, JSON.stringify(filteredEvents));
    return filteredEvents;
  }

  async syncToCloud(): Promise<void> {
    const {unlock} = await Mutex.getLock('games-sync-to-cloud').lock(false);
    try {
      //#region Ensure teams are synced to cloud (required for game and pbp sync mappings)
      let teamsMapByLocalId: { [key: string]: TeamModelDto } = {};
      let teamsMapByServerId: { [key: string]: TeamModelDto } = {};

      await this.core!.ldTeamsService.syncToCloud();
      const teams = await this.core!.ldTeamsService.getTeams();
      for (const team of teams) {
        teamsMapByLocalId[team.localId] = team;
        if (team.uuid) teamsMapByServerId[team.uuid] = team;
      }
      //#endregion

      //#region Sync games to cloud
      const elegibleLocalGamesToSync = await this.getGames();
      // The update mapper needs to know what the cloud already holds for a game so it never
      // overwrites a good gameModelSnapshot with an empty one (records created before the snapshot
      // was uploaded carry none locally). `getall` is the only place that list is available, so it
      // is captured on the way through.
      const cloudGameModelSnapshotsByUuid = new Map<string, string>();
      const syncedGames = await this.core!.storage.syncToCloud(elegibleLocalGamesToSync, {
        getall: async () => {
          const cloudGames = await firstValueFrom(this.core!.api.gameTracker.gameTrackerControllerGetGames());
          cloudGameModelSnapshotsByUuid.clear();
          for (const cloudGame of cloudGames) if (cloudGame.uuid) cloudGameModelSnapshotsByUuid.set(cloudGame.uuid, cloudGame.gameModelSnapshot);
          return cloudGames;
        },
        create: async (game) => firstValueFrom(this.core!.api.gameTracker.gameTrackerControllerCreateGame({ body: await mapLocalGameToCloudGame(game, teamsMapByLocalId, await this.getGameEvents(game.localId)) })),
        delete: (game) => firstValueFrom(this.core!.api.gameTracker.gameTrackerControllerDeleteGame({ uuid: game.uuid! })),
        update: async (game) => firstValueFrom(this.core!.api.gameTracker.gameTrackerControllerUpdateGame({ uuid: game.uuid!, body: await mapLocalGameToCloudGame(game, teamsMapByLocalId, game.trackerMode === 'camera' && game.gameStatus === 'ended' ? await this.getGameEvents(game.localId) : undefined, undefined, cloudGameModelSnapshotsByUuid.get(game.uuid!)) })),
      }, (cloudGame, localGame) => mapCloudGameToLocalGame(cloudGame, teamsMapByServerId, localGame), {
        create: (localGame) => {
          // Extra check for creating games, if the game is not tracker mode == camera and game status is ended, we don't want to sync it to the cloud
          return localGame.trackerMode === 'camera' && localGame.gameStatus === 'ended';
        },
      });
      //#endregion

      //#region Sync local camera games pbp from cloud to fetch pbp uuid and update local pbp with it
      const localCameraGames = syncedGames.filter(game => game.trackerMode === 'camera' && game.gameStatus === 'ended' && game.uuid);
      console.log('localCameraGames', localCameraGames);
      for (const game of localCameraGames) {
        const gameEvents = await this.getGameEvents(game.localId);
        if (gameEvents.length === 0) continue; // Nothing to sync
        const gameEventsMap = gameEvents.reduce((acc, event) => {
          acc[event.orderId] = event;
          return acc;
        }, {} as { [key: number]: EventDto });

        const cloudGamePbp = await firstValueFrom(this.core!.api.gameTracker.gameTrackerControllerGetGamePbp({ uuid: game.uuid! }));
        if (cloudGamePbp.length === gameEvents.length) {
          // Sync pbp from cloud to local
          for (const pbp of cloudGamePbp) {
            const localPbp = gameEventsMap[pbp.orderId];
            if (localPbp && localPbp.localId === pbp.localId && localPbp.important === pbp.important) {
              localPbp.uuid = pbp.uuid;
              localPbp.failedVideoUploaded = undefined; // TODO: Maybe we should not reset this on all sync attempts
              await this.updateGameEvent(localPbp);
            } else {
              console.log('Cloud vs local events info mismatch. Mark for full game sync (this will be done in the next sync)', game.localId, pbp.orderId);
              game.localLastUpdate = Date.now();
            }
          }
        } else {
          console.log('Cloud vs local event number mismatch. Mark for full game sync (this will be done in the next sync)', game.localId);
          game.localLastUpdate = Date.now();
        }

        if (!game.localLastUpdate) {
          // Add to post live video upload manager
          const uploadManagers = this.postLiveUploadManagerSubject.value;
          const uploadManager = uploadManagers.find(manager => manager.game.localId === game.localId);
          if (!uploadManager) uploadManagers.push({ game, events: gameEvents, status: 'pending', progress: 0 });
          this.postLiveUploadManagerSubject.next(uploadManagers);
        }
      }
      //#endregion

      await this.updateGames(syncedGames, true);
      this.uploadPostLiveVideoTick();
    } catch (error) {
      console.error('Fatal error syncing games to cloud', error);
      throw error;
    } finally {
      unlock(true);
    }
  }

  public async uploadEventVideo(event: EventDto, game: GameDto): Promise<void> {
    // Upload video
    const file = event.videoBlob ?? await this.core!.ldFilesService.getVideoFromShareFilesystem(game.localId, event.videoUri);
    const videoUploadUrl = await firstValueFrom(this.core!.api.gameTracker.gameTrackerControllerUploadGamePbpCut({
      uuid: game.uuid!,
      pbpUuid: event.uuid!,
      body: {
        fileName: event.videoUri,
        mimetype: file.type,
        size: file.size,
      }
    }));

    const videoUploadResponse = await fetch(videoUploadUrl.videoUrl!, {
      method: 'PUT',
      body: file,
      headers: {
        'ngsw-bypass': 'true',
      }
    });
    if (!videoUploadResponse.ok) {
      console.error('Error uploading video', videoUploadResponse);
      throw new Error('Error uploading video');
    }

    // Notifiy api that video was uploaded
    const confirmUploadResponse = await firstValueFrom(this.core!.api.gameTracker.gameTrackerControllerUploadGamePbpCutConfirm({ uuid: game.uuid!, pbpUuid: event.uuid! }));

    event.lastPresignedCloudVideoUrl = confirmUploadResponse.videoUrl ?? undefined;
    if (event.lastPresignedCloudVideoUrl) event.lastPresignedCloudVideoTimestamp = Date.now();
    event.cloudVideoUploaded = true;
  }

  async uploadPostLiveVideoTick(): Promise<void> {
    let uploadOnTick = false;
    const {unlock} = await Mutex.getLock('upload-post-live-video').lock(false);
    try {

      const checkUploadEnd = async (upload: { game: GameDto, events: EventDto[], status: 'pending' | 'uploading' | 'done', progress: number }) => {
        upload.progress = (upload.events.filter(e => e.cloudVideoUploaded).length / upload.events.length) * 100;
        if (upload.progress >= 100) {
          upload.status = 'done';
          upload.game.trackerMode = 'bench';
          await this.updateGame(upload.game);
          this.postLiveUploadManagerSubject.next(postLiveUploadManager);
        }
      }

      const postLiveUploadManager = this.postLiveUploadManagerSubject.value.filter(upload =>
        upload.status !== 'done' &&
        upload.events.filter(event => !event.cloudVideoUploaded && !event.failedVideoUploaded).length > 0
      ).sort((a, b) => a.progress - b.progress);
      const currentUpload = postLiveUploadManager[0];
      if (currentUpload) {
        const event = currentUpload.events.filter(event => !event.cloudVideoUploaded && !event.failedVideoUploaded).sort((a, b) => a.orderId - b.orderId)[0];
        if (event) {
          uploadOnTick = true;
          try {
            await this.uploadEventVideo(event, currentUpload.game);
          } catch (error) {
            console.error('Error uploading event video', error);
            event.failedVideoUploaded = Date.now();
            throw error;
          } finally {
            await this.updateGameEvent(event);
          }
        }

        checkUploadEnd(currentUpload);
      }

      this.postLiveUploadManagerSubject.value.filter(upload =>
        upload.status !== 'done' &&
        upload.game.trackerMode === 'camera' &&
        upload.game.gameStatus === 'ended'
      ).forEach(async upload => {
        console.log('Marking upload as done', upload);
        checkUploadEnd(upload);
      });

      console.log('postLiveUploadManager', this.postLiveUploadManagerSubject.value);
    } catch (error) {
      console.error('Fatal error uploading post live video', error);
      throw error;
    } finally {
      unlock(true);
      if (uploadOnTick) this.uploadPostLiveVideoTick();
    }
  }

}

